import { isEqual } from 'lodash';
import { initialize } from 'redux-form';
import { ActionCreators as UndoActionCreators } from 'redux-undo';
import { generatePath } from 'react-router-dom';
import type { CardEditorType, StoryModel } from 'types/story';
import { STORY_VERSIONS } from 'common/constants';
import { rebaseStoryEdits } from 'common/story-edit-operations/story-document';
import type { AdminThunkAction, AdminThunkDispatch, AdminReducerState } from 'admin/reducers';
import { selectEditableStateInfo } from 'admin/reducers/card-editor/selectors';
import { FORM_MODEL } from 'admin/constants/common';
import { STORY_EDIT_OPERATIONS } from 'admin/constants/actions';
import { STORY_FLOW_PAGE } from 'admin/constants/routes';
import { toast } from 'admin/components/common/MessageContainer/toast';
import { fetchStory } from 'admin/actions/story/get-story';
import { refreshEditedCard } from 'admin/actions/card-editor/refresh-edited-card';
import { isEditOperationsSave } from 'admin/utils/story-save-mechanism';
import { storyEditSaverFor } from 'admin/utils/story-edit-operations/saver';
import { adminHistory } from 'admin/utils/admin-history';

/** What the card editor's undo history restores; another writer's change to any of it clears the history. */
const hasEditedCardChanged = (before: CardEditorType, after: CardEditorType) =>
	!isEqual(before.data, after.data) ||
	!isEqual(before.storyElements, after.storyElements) ||
	!isEqual(before.symbols, after.symbols);

const isLatestOf = (state: AdminReducerState, storyId: string) =>
	state.storyEditor.story?.id === storyId && state.version.current === STORY_VERSIONS.latest;

/** Refreshes the open card; leaves the card editor, like its Flow link, when the card no longer exists. */
function catchUpEditedCard(dispatch: AdminThunkDispatch, getState: () => AdminReducerState, story: StoryModel) {
	const before = getState().cardEditor.present;
	const cardId = before.data?._id;
	if (!cardId) return;

	const isRefreshed = refreshEditedCard(dispatch, {
		story,
		cardId,
		editableState: () => selectEditableStateInfo(getState()),
	});
	if (!isRefreshed) {
		toast.warn('This card was deleted by another editor.');
		adminHistory.push(generatePath(STORY_FLOW_PAGE, { storyId: story.id }));
		return;
	}

	if (hasEditedCardChanged(before, getState().cardEditor.present)) {
		dispatch(UndoActionCreators.clearHistory());
	}
}

/**
 * Replaces the editor's state with the latest story plus this tab's unacknowledged actions
 * (see Staying current in docs/specs/story-edit-operations.md).
 */
function runCatchUp(): AdminThunkAction {
	return async (dispatch, getState) => {
		const loadedStory = getState().storyEditor.story;
		if (!isEditOperationsSave || !loadedStory || !isLatestOf(getState(), loadedStory.id)) return;
		const storyId = loadedStory.id;
		const saver = storyEditSaverFor(storyId);

		// No request may start before the caught-up state is in place, and none may be in flight
		// during the fetch: its actions could be both in the fetched story and re-applied.
		const release = saver?.hold();
		try {
			await saver?.whenNoRequestInFlight();
			// Not stored as fetched: the editor would show it without the pending actions, and the card editor
			// would leave for "not found" if the edited card was deleted.
			const { result: fetched } = await dispatch(fetchStory({ storyId }));
			if (!fetched) throw new Error('The latest story could not be loaded.');
			// The editor may have left the story or its latest version meanwhile.
			if (!isLatestOf(getState(), storyId)) return;

			const { story } = rebaseStoryEdits(fetched, saver?.pending ?? []);
			dispatch({ type: STORY_EDIT_OPERATIONS.CAUGHT_UP, payload: { story } });
			// Keeps the values being typed in an open Story Settings modal; they are saved on submit.
			dispatch(initialize(FORM_MODEL.EDIT_STORY, story, { keepDirty: true }));
			catchUpEditedCard(dispatch, getState, story);
		} finally {
			release?.();
		}
	};
}

let running: Promise<void> | undefined;
let scheduled: Promise<void> | undefined;

/**
 * Coalesced: one catch-up runs at a time; a request during a run schedules exactly one more.
 * With `storyId`, does nothing unless that story is the loaded one: a saver can outlive its story.
 */
export function catchUpStory(storyId?: string): AdminThunkAction {
	return async (dispatch, getState) => {
		if (storyId && getState().storyEditor.story?.id !== storyId) return;
		if (!running) {
			running = runCatchUp()(dispatch, getState).finally(() => {
				running = undefined;
			});
			await running;
			return;
		}
		scheduled ??= running
			.catch(() => undefined)
			.then(() => {
				scheduled = undefined;
				return catchUpStory()(dispatch, getState);
			});
		await scheduled;
	};
}
